/**
 * Site dashboard layout — shared dashboard shell for site operational subpages.
 *
 * Route-mounted parent for the site dashboard and dashboard-adjacent subpages
 * that should keep the same context rail. Owns the dashboard data subscription,
 * rail command affordances, mobile summary, and shell chrome; child routes
 * render only the main stage through Outlet context.
 *
 * Contains:
 * - SiteDashboardLayout — route layout component (WindowSizeHOC-wrapped default export)
 * - useSiteDashboardLayoutContext — child route access to shared dashboard state
 */
import { Capacitor } from '@capacitor/core';
import { IonContent, IonRefresher } from '@ionic/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import ScopeContext from '@sophocles/app/ScopeContext';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import SmallBadge from '@sophocles/components/SmallBadge';
import WindowSizeHOC from '@sophocles/components/WindowSizeHOC';
import { selectSiteCommandActivity, selectSiteCommandLocked } from '@sophocles/features/entities/site';
import { getSelectedPlantSelector } from '@sophocles/features/sites/slices/selectedPlant/selectors';
import { extraStructureFields } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import { unshareVirtualAccess } from '@sophocles/features/virtual/api';
import { determineIsAtLeastSiteGroupAdmin, userPermCheck, windowSizes } from '@sophocles/utils';
import CloneUnitModal from '@sophocles/views/sites/components/CloneUnitModal';
import AlarmsSummaryCard from '@sophocles/views/sites/components/SiteDashboard/AlarmsSummaryCard';
import CommandActivityIndicator from '@sophocles/views/sites/components/SiteDashboard/CommandActivityIndicator';
import {
  buildCommandRelays,
  deriveSiteCardStatus,
  filterVisibleAlarms,
  getAlarmStatus,
  sortRelays,
} from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import MaintenanceSummaryCard from '@sophocles/views/sites/components/SiteDashboard/MaintenanceSummaryCard';
import MobileSiteSummaryBar from '@sophocles/views/sites/components/SiteDashboard/MobileSiteSummaryBar';
import SiteDashboardRail from '@sophocles/views/sites/components/SiteDashboard/SiteDashboardRail';
import SiteDashboardShell from '@sophocles/views/sites/components/SiteDashboard/SiteDashboardShell';
import SiteQuickActions, { COMMAND_BUTTON_SX } from '@sophocles/views/sites/components/SiteDashboard/SiteQuickActions';
import DeactivateSiteDialog from '@sophocles/views/sites/components/StructureCard/DeactivateSiteDialog';
import MoveSiteDialog from '@sophocles/views/sites/components/StructureCard/MoveSiteDialog';
import { USE_MAP_BACKDROP } from '@sophocles/views/sites/components/StructureCard/SiteMapBackground';
import StructureCardConfigureDialog from '@sophocles/views/sites/components/StructureCard/StructureCardConfigureDialog';
import { hasVisibleExtraFields } from '@sophocles/views/sites/components/StructureCard/StructureDetailsList';
import UnitCommandsPanelDialog from '@sophocles/views/sites/components/UnitCommandsPanelDialog';
import useDashboardBlades from '@sophocles/views/sites/hooks/useDashboardBlades';
import useSiteDashboardData from '@sophocles/views/sites/hooks/useSiteDashboardData';
import useSiteViewPreferences from '@sophocles/views/sites/hooks/useSiteViewPreferences';
import SiteDashboardLayoutContext, {
  useSiteDashboardLayoutContext,
} from '@sophocles/views/sites/SiteDashboardLayoutContext';
import { resolveDeleteSiteLabel } from '@sophocles/views/sites/utils/deleteSiteLabel';

// Per-subroute chrome + rail behavior, keyed by the relative path under the
// site dashboard base (e.g. `readings/power/consumption`). '' is the
// dashboard index; the rail stays pinned open there. Configuration/media/
// deviceimages keep today's behavior (collapsible, open by default,
// page-scroll stage). Grid/chart/list subpages default the rail collapsed
// (their content wants the width) and bound the stage to the viewport so it
// scrolls internally instead of the page; form/detail subpages keep the rail
// open and let the page scroll.
//
// `page` reuses the exact AppNav `page` value the target component renders
// today (even where that's a pre-existing inconsistency, e.g. `structure`
// with no suffix, or `site_settings` not matching LeftNav's
// `structure_settings` check) — preserving those quirks is intentional here;
// fixing them is a separate change once the components stop rendering their
// own chrome.
//
// Resolved via `resolveSubroutePath` (longest-prefix match), so a dynamic
// segment like `settings/:section` falls through to the `settings` entry
// without needing one entry per section value.
const SUBROUTE_CONFIG = {
  '': { page: 'structure_dashboard', collapsible: false, defaultCollapsed: false, stageScroll: 'page' },
  configuration: { page: 'structure_dashboard', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  media: { page: 'structure_dashboard', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  deviceimages: { page: 'structure_dashboard', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  pop: { page: 'structure_pop', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  statushist: { page: 'structure_statushist', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  calendar: { page: 'site_calendar', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  settings: { page: 'site_settings', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  maintenancehist: {
    page: 'structure_maintenancehist',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  alarmhist: { page: 'structure_alarmhist', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  compliance: { page: 'structure_compliance', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  'activity-log': {
    page: 'structure_activity_log',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  'command-log': { page: 'structure_command_log', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  statuses: { page: 'structure_statuses', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  thresholds: { page: 'structure', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  schedulecommands: { page: 'structure', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  cicstatushist: {
    page: 'structure_cicstatushist',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  charting: { page: 'site_charting', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  // ReadingsRouterView (`readings`, `readings/power`, `measurements`) picks
  // MeasurementDataView (`site_measurements`) or the legacy PowerDataView
  // (`site_power`) at runtime based on protocol version / feature flag — the
  // layout can't resolve that without duplicating the check, so these three
  // share the `structure_power` id used by the rest of the power family.
  readings: { page: 'structure_power', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  'readings/power': { page: 'structure_power', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  measurements: { page: 'structure_power', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
  'readings/power/meter': {
    page: 'structure_power',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  'readings/power/consumption': {
    page: 'structure_power',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  'readings/power/solar': {
    page: 'structure_power',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  'readings/power/ansi': {
    page: 'structure_power',
    collapsible: true,
    defaultCollapsed: true,
    stageScroll: 'contained',
  },
  solaroptions: { page: 'structure_dashboard', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  'ticket/new': { page: 'plant_tickets', collapsible: true, defaultCollapsed: true, stageScroll: 'page' },
  tickets: { page: 'structure_tickets', collapsible: true, defaultCollapsed: true, stageScroll: 'contained' },
};

// Resolves a relative subpath (e.g. `readings/power/consumption`) against a
// path-keyed config map by trying the full path first, then progressively
// shorter prefixes, falling back to `fallback` if nothing matches. This is
// what lets a dynamic segment (`settings/:section`) resolve through its
// parent's static entry (`settings`) without one entry per section value.
function resolveSubroutePath(configMap, subroutePath, fallback) {
  const segments = subroutePath.split('/').filter(Boolean);
  for (let i = segments.length; i >= 0; i -= 1) {
    const key = segments.slice(0, i).join('/');
    if (Object.prototype.hasOwnProperty.call(configMap, key)) return configMap[key];
  }
  return fallback;
}

export { useSiteDashboardLayoutContext };

function SiteDashboardLayout(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNativePlatform = Capacitor.isNativePlatform();
  const { t } = useTranslation();
  const refresher = useRef(null);

  const {
    match: { params },
    windowWidth,
    location,
  } = props;

  const siteId = params.site_id;

  const {
    customer,
    selectedStructure,
    loading,
    dashboardError,
    unit,
    site,
    alarms,
    weather,
    capabilities,
    sitePermissions,
    curfew,
    alarmDefs,
    refetch,
  } = useSiteDashboardData(siteId, params);

  const blades = useDashboardBlades();
  // Seeds the structure slice's extraStructureFields / scheduleView from the
  // saved view preferences on every dashboard mount — not only while
  // StructureCardConfigureDialog happens to be open — so StructureCard
  // reflects the user's saved configuration immediately.
  useSiteViewPreferences();
  // useUser() for the legacy Immutable user Map needed by canRelocate /
  // MoveSiteDialog (virtual relocate path + permission checks).
  const { user } = useUser();
  // selectedPlant is the legacy Immutable site-group entity — MoveSiteDialog
  // reads siteGroup.get('customer')/.get('id') for the move payload, and it's
  // exposed via context as the siteGroup arg legacy alarm/history dialogs expect.
  const selectedPlant = useSelector(getSelectedPlantSelector);
  // fieldVisibility: Immutable OrderedMap driving the per-field show toggle in
  // StructureDetailsList. Passed to hasVisibleExtraFields so the divider
  // appears only when at least one extra field is actually shown.
  const fieldVisibility = useSelector(extraStructureFields);
  const separateTenant = useSelector(selectSeparateTenant);
  const commandActivity = useSelector(selectSiteCommandActivity(siteId));
  const commandLocked = useSelector(selectSiteCommandLocked(siteId));

  const isStaff = capabilities.is_staff ?? false;
  const isSuperUser = user.get('is_superuser', false);
  // Per-(user,site) gates: prefer the per-site value from meta.permissions;
  // fall back to the global capability when the per-site block is absent.
  // The keys from meta.permissions share exact names with the global block.
  const siteCapabilities = sitePermissions ? { ...capabilities, ...sitePermissions } : capabilities;
  const canSendCommand = siteCapabilities.can_send_command ?? false;
  const canCloneUnit = siteCapabilities.can_clone_unit ?? false;
  const canManageUnit = siteCapabilities.can_manage_unit ?? false;
  const canCaptureCamera = siteCapabilities.can_capture_camera ?? false;
  const canEditCamera = siteCapabilities.can_edit_camera ?? false;
  const canAddCameraSchedule = siteCapabilities.can_add_cameraschedule ?? false;
  const canSendIntrusionCommands = siteCapabilities.can_send_intrusion_commands ?? false;
  const canEditIntrusionSettings = siteCapabilities.can_edit_intrusion_settings ?? false;
  const canEditIntrusionInputs = siteCapabilities.can_edit_intrusion_inputs ?? false;
  const canManageIntrusionConfiguration = siteCapabilities.can_manage_intrusion_configuration ?? false;
  const canConfigureSensors = siteCapabilities.can_configure_sensors ?? false;

  // extra_fields from the site snapshot (plain dict from selectSiteData).
  // The map backdrop visually separates the structure block from the unit
  // info; in plain-details mode the divider has to do that job.
  const showStructureUnitDivider = !USE_MAP_BACKDROP || hasVisibleExtraFields(site?.extra_fields, fieldVisibility);

  const isBillboard = site?.is_billboard ?? false;
  const relays = useMemo(() => sortRelays(unit?.relays), [unit]);
  const addonCic = unit?.cic ?? null;
  const commandUnitId = unit?.id ?? addonCic?.id ?? null;
  // Hardware model of whichever device the commands dialog targets — the
  // firmware picker refuses images built for a different base model.
  const commandUnitModelId = (unit?.id != null ? unit?.model?.id : addonCic?.model?.id) ?? null;
  const commandRelays = useMemo(() => buildCommandRelays(relays, t('measurements.relay', 'Relay')), [relays, t]);

  // Admin-only alarm visibility follows the backend `view_admin_alarms` gate
  // (exposed as capabilities.can_view_admin_alarms); fall back to the staff/
  // super flags while the capability may be absent from an older payload.
  const canViewAdminAlarms = capabilities.can_view_admin_alarms ?? (isStaff || isSuperUser);
  const visibleAlarms = useMemo(
    () => filterVisibleAlarms(alarms, alarmDefs, canViewAdminAlarms),
    [alarms, alarmDefs, canViewAdminAlarms],
  );
  const alarmCount = visibleAlarms.length;
  const alarmStatus = useMemo(() => getAlarmStatus(visibleAlarms, alarmDefs), [visibleAlarms, alarmDefs]);

  const onMobile = windowWidth <= windowSizes.mobile.width;
  const deactivated = site?.deactivated ?? false;
  // siteName / siteLocation from site snapshot; selectedStructure retained as
  // fallback for MoveSiteDialog (legacy write path — leave unchanged).
  const siteName = site?.name ?? selectedStructure.get('name', '');
  const siteLocation = site?.location_description ?? selectedStructure.get('location_description', '');
  const deviceName =
    unit?.model?.display_name ?? selectedStructure.getIn(['relay_controller', 'model', 'display_name'], '');
  const hasDevice = unit != null || selectedStructure.get('relay_controller', null) != null;
  const radioOnline = unit?.radio?.online ?? null;
  // Drives the rail header dot beside the site name.
  const siteStatus = deriveSiteCardStatus({
    hasDevice,
    hasRadio: unit?.radio != null,
    hasEverReported: unit?.last_active != null,
    isRadioOnline: radioOnline,
    alarmCount,
    alarmStatus,
  });
  const siteIccid = unit?.radio?.iccid ?? null;
  const userGroups = user.get('groups', []);
  const canRelocate =
    user.get('is_staff', false) ||
    determineIsAtLeastSiteGroupAdmin(userGroups) ||
    userPermCheck(user, 'pliny.change_sitegroupmembership');
  const relocateLabel = isBillboard
    ? t('relocate.relocateStructure', 'Relocate structure')
    : t('relocate.relocateSite', 'Relocate site');

  // Delete (deactivate / virtual unshare) lives with the other site actions
  // up top; below md — where this bar is hidden — StructureCard keeps its own
  // delete icon in the details sheet. The dialog distinguishes the virtual
  // unshare flow from a real deactivation.
  const [deleteSiteOpen, setDeleteSiteOpen] = useState(false);
  const canDeleteSite = userPermCheck(user, 'pliny.delete_site');
  // "Virtual" is a property of the membership the route is standing in, not of
  // the site: the snapshot's site.is_virtual reports the *primary* group, which
  // is never virtual. selectedPlant is the route's :sitegroup_id group, so it is
  // the only source that distinguishes a shared view from the owner's view —
  // StructureCard reads the same one.
  const isVirtualSite = selectedPlant.get('is_virtual', false);
  const deleteSiteLabel = resolveDeleteSiteLabel(t, { isVirtual: isVirtualSite, isBillboard });

  // Subpages (configuration / media / device images / …) render through the layout's
  // Outlet. Detecting which one lets us (a) label the breadcrumb leaf — which also
  // turns the structure crumb into a link back to the dashboard — and (b) show an
  // explicit back-to-dashboard control. Empty string means the dashboard index.
  // `subroute` is the full relative path (not just the first segment) so the
  // `readings/power/*` family and `settings/:section` resolve unambiguously
  // via resolveSubroutePath below.
  const dashboardBasePath = `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}`;
  const subroute = location.pathname.slice(dashboardBasePath.length).replace(/^\//, '');
  const subrouteTitles = {
    configuration: t('deviceConfig.title', 'Device Configuration'),
    media: t('media.media', 'Media'),
    deviceimages: t('pages.deviceImages', 'Device Images'),
    pop: t('sites.proofOfPerformance'),
    statushist: t('relayController.statusHistory'),
    calendar: t('calendar.calendarScheduling', 'Calendar Scheduling'),
    settings: t('siteSettings.v2.title', 'Settings'),
    maintenancehist: t('sites.maintenanceHistory', 'Maintenance History'),
    alarmhist: t('sites.alarmHistory', 'Alarm History'),
    // SiteComplianceView builds this same "<site> - Proof of Performance"
    // title dynamically; siteName is already in scope here.
    compliance: `${siteName} - ${t('sites.proofOfPerformance', 'Proof of Performance')}`,
    'activity-log': t('sites.activityLog', 'Activity Log'),
    'command-log': t('sites.commandLog', 'Command Log'),
    statuses: `${t('sites.statusHistory', 'Status History')} (V2)`,
    thresholds: t('pages.relayControllerThresholds', 'Relay Controller Voltage Thresholds'),
    schedulecommands: t('pages.relayControllerScheduleCommands', 'Relay Controller Schedule Commands'),
    cicstatushist: t('pages.cicStatusHistory', 'CIC Status History'),
    charting: t('charting.title', 'Charting'),
    // See the SUBROUTE_CONFIG comment above — ReadingsRouterView's actual
    // heading depends on a runtime protocol/feature check the layout can't
    // replicate statically, so the three routes it can mount at share one title.
    readings: t('sites.powerReadings', 'Power Readings'),
    'readings/power': t('sites.powerReadings', 'Power Readings'),
    measurements: t('sites.powerReadings', 'Power Readings'),
    'readings/power/meter': t('pages.internationalMeterReadings', 'International Meter Readings'),
    'readings/power/consumption': t('pages.powerConsumption', 'Power Consumption'),
    'readings/power/solar': t('pages.solarPowerReadings', 'Solar Power Readings'),
    'readings/power/ansi': t('pages.ansiPowerMetering', 'ANSI Power Metering'),
    // Matches SolarOptionsPage's own hardcoded (non-i18n) title verbatim.
    solaroptions: 'Solar Options',
    'ticket/new': t('support.newTicket', 'New Ticket'),
    // SiteTicketGridView's real title varies "Structure"/"Site" by tenant
    // billboard flag; this static fallback matches its base translation.
    tickets: t('tickets.ticketList', 'Tickets'),
  };
  const subrouteTitle = resolveSubroutePath(subrouteTitles, subroute, null);
  const { page, collapsible, defaultCollapsed, stageScroll } = resolveSubroutePath(
    SUBROUTE_CONFIG,
    subroute,
    SUBROUTE_CONFIG[''],
  );

  // Each subpage opens its rail at its own default; not persisted across
  // reloads or navigation between subpages.
  const [railCollapsed, setRailCollapsed] = useState(defaultCollapsed);
  useEffect(() => {
    setRailCollapsed(defaultCollapsed);
  }, [subroute, defaultCollapsed]);

  // Desktop-only rail show/hide toggle. Collapsing hides the rail entirely
  // (see SiteDashboardShell) — this is the only control for bringing it back,
  // so it's rendered whenever the subroute has a collapsible rail at all.
  const railToggleLabel = railCollapsed
    ? t('actions.showDetails', 'Show details')
    : t('actions.hideDetails', 'Hide details');
  const railToggleAction =
    collapsible && !onMobile ? (
      <Tooltip title={railToggleLabel}>
        <IconButton aria-label={railToggleLabel} onClick={() => setRailCollapsed(prev => !prev)}>
          {railCollapsed ? <ViewSidebarOutlinedIcon /> : <ViewSidebarIcon />}
        </IconButton>
      </Tooltip>
    ) : null;

  const topBarActions = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <CommandActivityIndicator
        activity={commandActivity}
        sx={{ display: { xs: 'none', lg: 'inline-flex' }, mr: 'var(--space-1)' }}
      />
      {railToggleAction}

      {canRelocate && params.sitegroup_id != null && params.site_id != null ? (
        <Tooltip title={relocateLabel}>
          <IconButton aria-label={relocateLabel} onClick={blades.openRelocate}>
            <DriveFileMoveOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : null}

      {!separateTenant && (
        <Tooltip title={t('configureDisplay.configureYourView', 'Configure Display')}>
          <IconButton
            aria-label={t('configureDisplay.configureYourView', 'Configure Display')}
            onClick={blades.openConfigureDisplay}
          >
            <TuneIcon />
          </IconButton>
        </Tooltip>
      )}

      {!isNativePlatform && canCloneUnit && unit != null && !separateTenant ? (
        <CloneUnitModal
          initialSite={siteId}
          triggerItem={
            <Tooltip title={t('sites.cloneSettings')}>
              <IconButton aria-label={t('sites.cloneSettings')}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          }
        />
      ) : null}

      {!__EXCLUDE_RETURNS__ && !isNativePlatform && isStaff ? (
        <Link to={`/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/ticket/new`}>
          <SmallBadge
            overlap="circular"
            badgeContent="+"
            color="primary"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Tooltip title={t('general.newTicket')}>
              <IconButton aria-label={t('general.newTicket')}>
                <Icon fontSize="small" className="fas fa-ticket-alt" />
              </IconButton>
            </Tooltip>
          </SmallBadge>
        </Link>
      ) : null}

      {canDeleteSite && site != null ? (
        <Tooltip title={deleteSiteLabel}>
          <IconButton aria-label={deleteSiteLabel} onClick={() => setDeleteSiteOpen(true)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );

  const moreCommandsAction =
    (isStaff || isSuperUser) && canSendCommand && commandUnitId != null ? (
      <Button
        size="small"
        variant="contained"
        color="primary"
        startIcon={<TerminalOutlinedIcon fontSize="small" />}
        disabled={commandLocked}
        onClick={blades.openCommands}
        sx={COMMAND_BUTTON_SX}
      >
        {t('commands.moreCommands', 'More Commands')}
      </Button>
    ) : null;

  // App-layer scope for useScope(), converted to plain JS at this boundary to
  // match CustomerLayout / SiteGroupLayout. The site dashboard also owns the
  // dashboard-specific SiteDashboardLayoutContext below.
  const scope = useMemo(
    () => ({
      customer: customer ? customer.toJS() : null,
      siteGroup: selectedPlant ? selectedPlant.toJS() : null,
      site: site ?? null,
      unit: unit ?? null,
    }),
    [customer, selectedPlant, site, unit],
  );

  const contextValue = useMemo(
    () => ({
      params,
      siteId,
      location,
      customer,
      selectedStructure,
      selectedPlant,
      loading,
      unit,
      site,
      alarms,
      weather,
      // siteCapabilities merges the global capabilities with the per-site
      // overrides from meta.permissions when available.
      capabilities: siteCapabilities,
      curfew,
      alarmDefs,
      isStaff,
      canSendCommand,
      commandLocked,
      canManageUnit,
      canCaptureCamera,
      canEditCamera,
      canAddCameraSchedule,
      canSendIntrusionCommands,
      canEditIntrusionSettings,
      canEditIntrusionInputs,
      canManageIntrusionConfiguration,
      canConfigureSensors,
      isBillboard,
      relays,
      addonCic,
      commandUnitId,
      commandRelays,
      siteIccid,
      blades,
      refetch,
    }),
    [
      params,
      siteId,
      location,
      customer,
      selectedStructure,
      selectedPlant,
      loading,
      unit,
      site,
      alarms,
      weather,
      siteCapabilities,
      curfew,
      alarmDefs,
      isStaff,
      canSendCommand,
      commandLocked,
      canManageUnit,
      canCaptureCamera,
      canEditCamera,
      canAddCameraSchedule,
      canSendIntrusionCommands,
      canEditIntrusionSettings,
      canEditIntrusionInputs,
      canManageIntrusionConfiguration,
      canConfigureSensors,
      isBillboard,
      relays,
      addonCic,
      commandUnitId,
      commandRelays,
      siteIccid,
      blades,
    ],
  );

  const content = (
    <>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, px: 2 }}>
        {subroute ? (
          <Tooltip title={t('actions.backToDashboard', 'Back to dashboard')}>
            <IconButton
              aria-label={t('actions.backToDashboard', 'Back to dashboard')}
              onClick={() => navigate(dashboardBasePath)}
              sx={{
                backgroundColor: 'var(--title-action-bg)',
                border: 1,
                borderColor: 'var(--title-action-border)',
                boxShadow: 'var(--card-shadow-raised)',
                '&:hover': {
                  backgroundColor: 'var(--title-action-hover)',
                  boxShadow: 'var(--card-shadow-raised-hover)',
                },
                '&:active': { boxShadow: 'var(--card-shadow-raised-active)' },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TitleBar showBackButton title={subrouteTitle} actions={topBarActions} />
        </Box>
      </Box>
      {dashboardError && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Alert
            severity="error"
            action={
              <Button size="small" color="inherit" onClick={() => refetch()}>
                {t('actions.retry', 'Retry')}
              </Button>
            }
          >
            {t('sites.dashboardLoadError', "Couldn't load this site. Check your connection and try again.")}
          </Alert>
        </Box>
      )}
      {blades.configureDisplayOpen ? (
        <StructureCardConfigureDialog open={blades.configureDisplayOpen} closeDialog={blades.closeConfigureDisplay} />
      ) : null}
      {deleteSiteOpen ? (
        <DeactivateSiteDialog
          open
          closeDialog={() => setDeleteSiteOpen(false)}
          dispatch={dispatch}
          router={{ navigate }}
          unshareVirtualAccess={data => dispatch(unshareVirtualAccess(data))}
          isVirtual={isVirtualSite}
          site={selectedStructure}
          siteGroup={selectedPlant}
          isBillboard={isBillboard}
        />
      ) : null}
      {blades.relocateOpen ? (
        <MoveSiteDialog
          open={blades.relocateOpen}
          closeDialog={blades.closeRelocate}
          site={site}
          siteGroup={selectedPlant}
          router={{ navigate }}
          user={user}
          isStaff={isStaff}
          isBillboard={isBillboard}
          onMobile={onMobile}
          capabilities={capabilities}
          isVirtual={isVirtualSite}
        />
      ) : null}
      <SiteDashboardShell
        collapsible={collapsible}
        collapsed={railCollapsed}
        stageScroll={stageScroll}
        summary={
          <MobileSiteSummaryBar
            name={siteName}
            location={siteLocation}
            deviceName={deviceName}
            hasDevice={hasDevice}
            online={radioOnline}
            alarmCount={alarmCount}
            alarmStatus={alarmStatus}
            commandActivity={commandActivity}
            deactivated={deactivated}
            quickActions={
              // On phones the commands live in the details sheet, where they
              // render with labels; the compact icon-only cluster is for the
              // tablet-width summary bar.
              commandUnitId != null && !onMobile ? (
                <SiteQuickActions siteId={siteId} canSendCommand={canSendCommand} compact />
              ) : null
            }
          />
        }
        rail={
          <SiteDashboardRail
            loading={loading}
            isBillboard={isBillboard}
            location={location}
            onMobile={onMobile}
            params={params}
            siteId={siteId}
            site={site}
            siteStatus={siteStatus}
            unit={unit}
            weatherConditions={weather}
            showStructureUnitDivider={showStructureUnitDivider}
            commandUnitId={commandUnitId}
            canSendCommand={canSendCommand}
            moreCommandsAction={moreCommandsAction}
            commandLogPath={`${dashboardBasePath}/command-log`}
          />
        }
        inlineExtrasOnPhone={!subroute}
        railExtras={
          loading ? null : (
            <>
              <AlarmsSummaryCard
                alarms={visibleAlarms}
                relays={relays}
                siteId={siteId}
                capabilities={siteCapabilities}
                historyPath={`${dashboardBasePath}/alarmhist`}
              />
              <MaintenanceSummaryCard
                siteId={siteId}
                onOpen={blades.openMaintenance}
                onAdd={blades.handleAddMaintenance}
                capabilities={siteCapabilities}
                historyPath={`${dashboardBasePath}/maintenancehist`}
              />
            </>
          )
        }
      >
        <ScopeContext.Provider value={scope}>
          <SiteDashboardLayoutContext.Provider value={contextValue}>
            <Outlet />
          </SiteDashboardLayoutContext.Provider>
        </ScopeContext.Provider>
      </SiteDashboardShell>
      <UnitCommandsPanelDialog
        open={blades.commandsOpen}
        onClose={blades.closeCommands}
        siteId={params.site_id}
        unitId={commandUnitId}
        unitModelId={commandUnitModelId}
        relays={commandRelays}
      />
    </>
  );

  const { loading: _loading, match: _match, ...realProps } = props;

  return (
    <AppNav
      flex
      page={page}
      notFullHeight={true}
      title={subrouteTitle ?? siteName}
      showBack={Boolean(subroute)}
      {...realProps}
    >
      <Box
        sx={{
          flexGrow: 1,
          // A flex item's default min-height is `auto`, so with height:100% and
          // a sibling mobile top bar it can't shrink to the available space —
          // it grows past it and AppNav's overflow:hidden clips the bottom.
          // minHeight:0 lets it shrink and scroll instead (fixes the cut-off
          // page bottom on mobile).
          minHeight: 0,
          overflowY: 'auto',
          // Clip any stray horizontal overflow so it can't spawn a page-level
          // horizontal scrollbar that eats vertical space and clips the bottom
          // of the dashboard. Inner scrollers (grids, the relay carousel) keep
          // their own horizontal scroll.
          overflowX: 'hidden',
          height: '100%',
          bgcolor: onMobile ? 'var(--surface-secondary)' : undefined,
        }}
      >
        {isNativePlatform ? (
          <IonContent slot="fixed" style={{ backgroundColor: 'transparent', '--background': 'none' }}>
            <IonRefresher
              ref={refresher}
              slot="fixed"
              onIonRefresh={() => {
                refetch().finally(() => {
                  if (refresher.current) {
                    refresher.current.complete();
                  }
                });
              }}
            />
            {content}
          </IonContent>
        ) : (
          content
        )}
      </Box>
    </AppNav>
  );
}

export default WindowSizeHOC(SiteDashboardLayout);
